import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../../axiosBaseQuery';
import lubricantCategoriesApiParser, {
  type LubricantCategoryListItem,
} from './lubricantCategoryApiParser';

// ---------- Types ----------
export type LubricantCategoriesListResponse = unknown;

export interface LubricantCategoriesListResult {
  data: LubricantCategoryListItem[];
}

interface CreateLubricantCategoryPayload {
  categoryName: string;
  primary: boolean;
  sortOrder: number;
  sumpTank: boolean;
}

// ---------- API Slice ----------
const lubricantCategoriesApi = createApi({
  reducerPath: 'lubricantCategoriesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['LubricantCategory'],
  endpoints: (builder) => ({
    getLubricantCategoriesList: builder.query<LubricantCategoriesListResult, any>({
      query: (params) => ({
        url: '/lubricant/category',
        method: 'GET',
        params: {
          ...params,
        },
      }),
      transformResponse: (res: LubricantCategoriesListResponse): LubricantCategoriesListResult => ({
        data: lubricantCategoriesApiParser.LubricantCategoriesListParser(res),
      }),
    }),

    createLubricantCategory: builder.mutation<any, CreateLubricantCategoryPayload>({
      query: (payload) => {
        const body = {
          showOrder: Number(payload.sortOrder) || 0,
          name: payload.categoryName,
          primary: Boolean(payload.primary),
          sumpTank: Boolean(payload.sumpTank),
        };
        return {
          url: '/lubricant/category',
          method: 'POST',
          data: body,
        };
      },
      invalidatesTags: ['LubricantCategory'],
    }),

    updateLubricantCategory: builder.mutation<
      any,
      { id: number; payload: CreateLubricantCategoryPayload }
    >({
      query: ({ id, payload }) => {
        const body = {
          showOrder: Number(payload.sortOrder) || 0,
          name: payload.categoryName,
          primary: Boolean(payload.primary),
          sumpTank: Boolean(payload.sumpTank),
        };
        return {
          url: `/lubricant/category/${encodeURIComponent(id)}`,
          method: 'PUT',
          data: body,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'LubricantCategory' as const, id: id.toString() },
        'LubricantCategory',
      ],
    }),

    deleteLubricantCategory: builder.mutation<any, number>({
      query: (id) => ({
        url: `/lubricant/category/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['LubricantCategory'],
    }),
  }),
});

// ---------- Exports ----------
export const lubricantCategoriesReducer = lubricantCategoriesApi.reducer;
export const lubricantCategoriesMiddleware = lubricantCategoriesApi.middleware;
export const lubricantCategoriesReducerPath = lubricantCategoriesApi.reducerPath;

export const {
  useGetLubricantCategoriesListQuery,
  useCreateLubricantCategoryMutation,
  useUpdateLubricantCategoryMutation,
  useDeleteLubricantCategoryMutation,
} = lubricantCategoriesApi;
